import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  CATEGORIES, CATEGORY_LABEL, UI_STRINGS, PRECISION_LABEL, formatDate,
} from "./lib.jsx";
import {
  CATEGORY_ICONS, ChevronRight, Close, Search, ExternalLink, Sun, Moon, Info,
} from "./icons.jsx";

/* ── LanguageToggle ─────────────────────────────────────────── */
export function LanguageToggle({ lang, onChange }) {
  return (
    <div className="lang-toggle" role="group" aria-label="Language">
      <button
        className={lang === "en" ? "is-active" : ""}
        onClick={() => onChange("en")}
        aria-pressed={lang === "en"}
      >EN</button>
      <button
        className={lang === "ru" ? "is-active" : ""}
        onClick={() => onChange("ru")}
        aria-pressed={lang === "ru"}
      >RU</button>
    </div>
  );
}

/* ── ThemeToggle ────────────────────────────────────────────── */
export function ThemeToggle({ theme, onChange }) {
  const next = theme === "dark" ? "light" : "dark";
  return (
    <button
      className="icon-btn"
      onClick={() => onChange(next)}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
    >
      {theme === "dark" ? <Sun /> : <Moon />}
    </button>
  );
}

/* ── FilterBar ──────────────────────────────────────────────── */
export function FilterBar({
  lang,
  categories, onToggleCategory, onResetCategories,
  tags, onToggleTag, allTags,
  query, onQuery,
  total, shown, onClearAll,
}) {
  const t = UI_STRINGS[lang];
  const searchRef = useRef(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [comboQuery, setComboQuery] = useState("");
  const comboRef = useRef(null);

  // ⌘K / ctrl-K
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // close combo on outside click
  useEffect(() => {
    function onClick(e) {
      if (!comboRef.current) return;
      if (!comboRef.current.contains(e.target)) setComboOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filteredTagOptions = useMemo(() => {
    const q = comboQuery.trim().toLowerCase();
    return allTags.filter(({ tag }) => !tags.has(tag) && (q === "" || tag.includes(q))).slice(0, 60);
  }, [comboQuery, allTags, tags]);

  const hasFilters = categories.size > 0 || tags.size > 0 || query.length > 0;

  return (
    <div className="filterbar-wrap">
      <div className="filterbar">
        <div className="filterbar-row">
          <label className="search">
            <Search className="search-icon" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder={t.search}
              aria-label={t.search}
            />
            <span className="search-kbd">⌘K</span>
          </label>

          <div className="combo" ref={comboRef}>
            <input
              type="text"
              value={comboQuery}
              onFocus={() => setComboOpen(true)}
              onChange={(e) => { setComboQuery(e.target.value); setComboOpen(true); }}
              placeholder={t.tagPlaceholder}
              aria-label={t.tagPlaceholder}
            />
            <ChevronRight className="combo-chev" style={{ transform: "translateY(-50%) rotate(90deg)" }} />
            {comboOpen && filteredTagOptions.length > 0 && (
              <div className="combo-menu" role="listbox">
                {filteredTagOptions.map(({ tag, count }) => (
                  <button
                    key={tag}
                    className="combo-item"
                    onClick={() => {
                      onToggleTag(tag);
                      setComboQuery("");
                      setComboOpen(false);
                    }}
                  >
                    <span>{tag}</span>
                    <span className="count">{count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="filterbar-row">
          <div className="cat-chips" role="group" aria-label="Categories">
            <button
              className={`cat-chip is-all ${categories.size === 0 ? "is-active" : ""}`}
              onClick={onResetCategories}
              aria-pressed={categories.size === 0}
            >
              {t.all}
            </button>
            {CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICONS[c];
              const active = categories.has(c);
              return (
                <button
                  key={c}
                  className={`cat-chip ${active ? "is-active" : ""}`}
                  style={{ "--c": `var(--cat-${c})` }}
                  onClick={() => onToggleCategory(c)}
                  aria-pressed={active}
                >
                  <span className="dot" />
                  <Icon style={{ color: `var(--cat-${c})` }} />
                  {CATEGORY_LABEL[c][lang]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="active-chips" aria-live="polite">
          {[...categories].map((c) => (
            <span className="active-chip" key={`c-${c}`}>
              <span className="label-kind">{t.cat}</span>
              <span style={{ color: `var(--cat-${c})` }}>{CATEGORY_LABEL[c][lang]}</span>
              <button onClick={() => onToggleCategory(c)} aria-label={`Remove category ${c}`}>
                <Close />
              </button>
            </span>
          ))}
          {[...tags].map((tg) => (
            <span className="active-chip" key={`t-${tg}`}>
              <span className="label-kind">{t.tag}</span>
              <span>{tg}</span>
              <button onClick={() => onToggleTag(tg)} aria-label={`Remove tag ${tg}`}>
                <Close />
              </button>
            </span>
          ))}
          {query.length > 0 && (
            <span className="active-chip">
              <span className="label-kind">{t.text}</span>
              <span>"{query.length > 22 ? query.slice(0, 22) + "…" : query}"</span>
              <button onClick={() => onQuery("")} aria-label="Clear search">
                <Close />
              </button>
            </span>
          )}
          {hasFilters && (
            <button className="clear-all" onClick={onClearAll}>{UI_STRINGS[lang].clearAll}</button>
          )}
          <span className="counter">
            {UI_STRINGS[lang].showing} {shown} {UI_STRINGS[lang].of} {total} {UI_STRINGS[lang].events}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── TimelineEvent ──────────────────────────────────────────── */
export function TimelineEvent({ event, lang, onToggleTag, activeTags }) {
  const [open, setOpen] = useState(false);
  const t = UI_STRINGS[lang];
  const Icon = CATEGORY_ICONS[event.category];
  const dateLabel = formatDate(event.date, event.date_precision, lang);
  const precisionLabel = PRECISION_LABEL[event.date_precision][lang];
  const confidence = event.confidence || "high";
  const showConfidence = confidence !== "high";

  return (
    <div
      className="event"
      data-confidence={confidence}
      style={{ "--c": `var(--cat-${event.category})` }}
    >
      <article className="event-card" style={{ "--c-bg": `var(--cat-${event.category}-bg)` }}>
        <div className="event-date">
          <span className="date-primary">{dateLabel}</span>
          <span className="date-precision" title={`Precision: ${precisionLabel}`}>{precisionLabel}</span>
        </div>

        <div className="event-body">
          <div className="event-head">
            <span className="event-cat">
              <Icon />
              {CATEGORY_LABEL[event.category][lang]}
            </span>
            {showConfidence && (
              <span className="event-confidence" tabIndex={0}>
                <Info />
                {confidence === "low" ? "low conf." : "med. conf."}
                <span className="tip">
                  {confidence === "low" ? t.confidenceLow : t.confidenceMedium}
                </span>
              </span>
            )}
          </div>

          <h3 className="event-title">{event.title[lang]}</h3>
          <p className="event-summary">{event.summary[lang]}</p>

          {event.tags?.length > 0 && (
            <div className="event-tags">
              {event.tags.map((tag) => (
                <button
                  key={tag}
                  className={`event-tag ${activeTags.has(tag) ? "is-active" : ""}`}
                  onClick={() => onToggleTag(tag)}
                  aria-label={`Filter by tag ${tag}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          <button
            className={`event-expand ${open ? "is-open" : ""}`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            <ChevronRight />
            {open ? t.collapse : t.expand}
          </button>

          <div className={`event-detail ${open ? "is-open" : ""}`}>
            <div className="event-detail-inner">
              <div className="event-detail-content">
                {event.impact?.[lang] && (
                  <div className="detail-block">
                    <div className="detail-label">{t.impact}</div>
                    <p>{event.impact[lang]}</p>
                  </div>
                )}
                {event.sources?.length > 0 && (
                  <div className="detail-block">
                    <div className="detail-label">{t.sources}</div>
                    <div className="detail-sources">
                      {event.sources.map((s, i) => (
                        <a
                          key={i}
                          className="detail-source"
                          href={s.url}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          <span className="source-id">[{String(i + 1).padStart(2, "0")}]</span>
                          {s.title}
                          <ExternalLink />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <span className="event-id">{event.id}</span>
      </article>
    </div>
  );
}

/* ── YearSection ────────────────────────────────────────────── */
export function YearSection({ year, items, lang, onToggleTag, activeTags }) {
  return (
    <section className="year-section" aria-labelledby={`year-${year}`}>
      <header className="year-header">
        <div className="year" id={`year-${year}`}>{year}</div>
        <div className="year-count">
          {items.length} {items.length === 1 ? "event" : "events"}
        </div>
      </header>
      <div className="event-rail">
        {items.map((e) => (
          <TimelineEvent
            key={e.id}
            event={e}
            lang={lang}
            onToggleTag={onToggleTag}
            activeTags={activeTags}
          />
        ))}
      </div>
    </section>
  );
}

/* ── EmptyState ─────────────────────────────────────────────── */
export function EmptyState({ lang, onClear }) {
  const t = UI_STRINGS[lang];
  return (
    <div className="empty">
      <div className="empty-icon"><Search /></div>
      <h3>{t.emptyTitle}</h3>
      <p>{t.emptyBody}</p>
      <button className="clear" onClick={onClear}>{t.emptyAction}</button>
    </div>
  );
}
